import { useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "../context/LanguageContext";

type ViewMode = "top" | "all";

interface DealsAccessSwitcherProps {
  viewMode?: ViewMode;
  topDealCount?: number;
  allDealCount?: number;
  onViewModeChange?: (mode: ViewMode) => void;
  showCrazyDeals?: boolean;
  crazyDealsActive?: boolean;
  /** Live count of crazy deals under the current filters, shown beside the label like Top / All. */
  crazyDealCount?: number;
  onCrazyDealsToggle?: (active: boolean) => void;
  showPennyDeals?: boolean;
  pennyDealsActive?: boolean;
  pennyDealCount?: number;
  onPennyDealsPress?: () => void;
  /** Dollar General: a Coupons tab that opens the coupon combos page. */
  onCouponsPress?: () => void;
  className?: string;
}

type Tab = {
  id: string;
  label: string;
  count?: number;
  selected: boolean;
  tone: "violet" | "orange" | "amber";
  role: "tab" | "button";
  pressed?: boolean;
  onPress: () => void;
};

const UNDERLINE_COLOR: Record<Tab["tone"], string> = {
  violet: "bg-violet-600 dark:bg-violet-400",
  orange: "bg-orange-500",
  amber: "bg-amber-500",
};
const ACTIVE_TEXT: Record<Tab["tone"], string> = {
  violet: "text-violet-700 dark:text-violet-300",
  orange: "text-orange-700 dark:text-orange-300",
  amber: "text-amber-700 dark:text-amber-300",
};

/**
 * One equal-width tab strip above the deal toolbar: Top and All, plus Crazy deals (Walmart) or
 * Penny deals (Home Depot, Dollar General) as a third tab. A single underline slides between
 * the tabs; the deep-discount tabs are toggles underneath, so leaving them turns the mode off.
 * Dollar General adds a Coupons tab (Top · All · Penny · Coupons) that opens coupon combos.
 */
export default function DealsAccessSwitcher({
  viewMode = "all",
  topDealCount,
  allDealCount,
  onViewModeChange,
  showCrazyDeals = false,
  crazyDealsActive = false,
  crazyDealCount,
  onCrazyDealsToggle,
  showPennyDeals = false,
  pennyDealsActive,
  pennyDealCount,
  onPennyDealsPress,
  onCouponsPress,
  className = "",
}: DealsAccessSwitcherProps) {
  const { t } = useTranslation();
  const hasCrazyDeals = showCrazyDeals && !!onCrazyDealsToggle;
  const hasPennyDeals = showPennyDeals && !!onPennyDealsPress;
  const pennyOn = pennyDealsActive === true;
  const feedSelected = !crazyDealsActive && !pennyOn;

  const selectFeed = (mode: ViewMode) => {
    if (crazyDealsActive) onCrazyDealsToggle?.(false);
    if (pennyOn) onPennyDealsPress?.();
    if (mode !== viewMode || crazyDealsActive || pennyOn) onViewModeChange?.(mode);
  };

  const tabs: Tab[] = [
    { id: "top", label: t("products.tabTop"), count: topDealCount, selected: feedSelected && viewMode === "top", tone: "violet", role: "tab", onPress: () => selectFeed("top") },
    { id: "all", label: t("products.tabAll"), count: allDealCount, selected: feedSelected && viewMode === "all", tone: "violet", role: "tab", onPress: () => selectFeed("all") },
    ...(hasCrazyDeals
      ? [{ id: "crazy", label: t("products.tabCrazyDeals"), count: crazyDealCount, selected: crazyDealsActive, tone: "orange" as const, role: "button" as const, pressed: crazyDealsActive, onPress: () => onCrazyDealsToggle?.(!crazyDealsActive) }]
      : []),
    ...(hasPennyDeals
      ? [{ id: "penny", label: onCouponsPress ? t("emptyStates.feed.tabPennyShort") : t("products.tabPennyDeals"), count: pennyDealCount, selected: pennyOn, tone: "amber" as const, role: "button" as const, pressed: pennyDealsActive === undefined ? undefined : pennyOn, onPress: () => onPennyDealsPress?.() }]
      : []),
    ...(onCouponsPress
      ? [{ id: "coupons", label: t("emptyStates.feed.tabCoupons"), selected: false, tone: "violet" as const, role: "button" as const, onPress: onCouponsPress }]
      : []),
  ];
  const selected = tabs.find((tab) => tab.selected);

  // Sliding underline: measure the selected tab and animate left/width with CSS.
  const stripRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const [underline, setUnderline] = useState<{ left: number; width: number } | null>(null);
  const selectedId = selected?.id;
  useLayoutEffect(() => {
    const el = selectedId ? tabRefs.current[selectedId] : null;
    const strip = stripRef.current;
    if (!el || !strip) return;
    const measure = () => setUnderline({ left: el.offsetLeft, width: el.offsetWidth });
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(strip);
    return () => observer.disconnect();
  }, [selectedId, tabs.length]);

  return (
    <div className={`relative flex min-h-11 items-stretch border-b border-stone-200 bg-white px-3 dark:border-stone-800 dark:bg-dark-900 sm:px-4 ${className}`}>
      <div ref={stripRef} role="tablist" aria-label={t("products.dealFeed")} className="relative flex min-w-0 flex-1">
        {tabs.map((tab) => {
          const countText = tab.count !== undefined ? tab.count.toLocaleString() : undefined;
          const a11y = tab.role === "tab"
            ? (countText ? t("products.tabDealsA11y", { label: tab.label, count: countText }) : t("products.tabDealsA11yNoCount", { label: tab.label }))
            : tab.label;
          return (
            <button
              key={tab.id}
              ref={(node) => { tabRefs.current[tab.id] = node; }}
              type="button"
              role={tab.role}
              aria-label={a11y}
              aria-selected={tab.role === "tab" ? tab.selected : undefined}
              aria-pressed={tab.role === "button" ? tab.pressed : undefined}
              disabled={tab.role === "tab" && tab.selected}
              onClick={tab.onPress}
              className={`flex min-w-0 flex-1 items-center justify-center gap-1.5 px-2 py-3 text-[13px] font-bold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-400 disabled:cursor-default ${
                tab.selected ? ACTIVE_TEXT[tab.tone] : "text-stone-500 hover:text-stone-900 dark:text-stone-400 dark:hover:text-white"
              }`}
            >
              <span className="whitespace-nowrap">{tab.label}</span>
              {countText ? (
                <span className={`shrink-0 tabular-nums text-[12px] font-semibold ${tab.selected ? "opacity-90" : "text-stone-400 dark:text-stone-500"}`}>{countText}</span>
              ) : null}
            </button>
          );
        })}
        {underline && selected ? (
          <span
            aria-hidden="true"
            className={`pointer-events-none absolute bottom-0 h-0.5 rounded-full transition-[left,width,background-color] duration-200 ease-out ${UNDERLINE_COLOR[selected.tone]}`}
            style={{ left: underline.left, width: underline.width }}
          />
        ) : null}
      </div>
    </div>
  );
}
